import { Alert, Button, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Icon, StatCard } from '@mysaiva/core'
import type { StatCardProps } from '@mysaiva/core'
import CodeGlyph from '../../assets/icon-code.svg?react'
import { useUsageSummary } from '../../usage/useUsageSummary'
import type { UsageSummary } from '../../usage/usage.types'
import {
  formatCount,
  formatMs,
  formatPercent,
  msChange,
  percentChange,
  pointChange
} from '../../usage/format'
import styles from './Dashboard.module.scss'

type CardId = 'apiCalls' | 'latency' | 'successRate' | 'keys'

const CARD_ICONS: Record<CardId, Pick<StatCardProps, 'icon' | 'iconTone'>> = {
  apiCalls: { icon: <CodeGlyph className={styles.usageIcon} />, iconTone: 'blue' },
  latency: { icon: <Icon icon="Clock" size={18} />, iconTone: 'pale-blue' },
  successRate: { icon: <Icon icon="CheckCircle" size={18} />, iconTone: 'blue' },
  keys: { icon: <Icon icon="Key" size={18} />, iconTone: 'pale-blue' }
}

function useCards(
  summary: UsageSummary | null
): Record<CardId, Omit<StatCardProps, 'icon' | 'iconTone'>> {
  const { t } = useTranslation()
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`developerPlatform.dashboard.usage.${key}`, options)

  const calls = summary
    ? percentChange(summary.apiCalls.total, summary.apiCalls.previousTotal)
    : null
  const latency = summary ? msChange(summary.latency.avgMs, summary.latency.previousAvgMs) : null
  const success = summary
    ? pointChange(summary.successRate.percent, summary.successRate.previousPercent)
    : null

  return {
    apiCalls: {
      value: summary ? formatCount(summary.apiCalls.total) : '',
      label: tr('apiCalls'),
      emptyHint: tr('emptyHint'),
      trend: calls
        ? {
            direction: calls.direction,
            tone: calls.raw >= 0 ? 'positive' : 'negative',
            value: calls.value,
            caption: tr('vsPrior', { days: summary?.periodDays ?? 30 })
          }
        : undefined
    },
    latency: {
      value: summary?.latency.avgMs != null ? formatMs(summary.latency.avgMs) : '—',
      unit: summary?.latency.avgMs != null ? 'ms' : undefined,
      label: tr('latency'),
      emptyHint: tr('emptyHint'),
      trend: latency
        ? {
            // Lower latency is the good direction.
            direction: latency.raw < 0 ? 'up' : 'down',
            tone: latency.raw < 0 ? 'positive' : 'negative',
            value: latency.value,
            caption: tr(latency.raw < 0 ? 'faster' : 'slower', { days: summary?.periodDays ?? 30 })
          }
        : undefined
    },
    successRate: {
      value:
        summary?.successRate.percent != null ? formatPercent(summary.successRate.percent) : '—',
      unit: summary?.successRate.percent != null ? '%' : undefined,
      label: tr('successRate'),
      emptyHint: tr('emptyHint'),
      trend: summary
        ? {
            direction: success?.direction,
            tone: success ? (success.raw >= 0 ? 'positive' : 'negative') : 'neutral',
            value: success?.value ?? '',
            caption: tr('errors', { count: summary.successRate.errorCount })
          }
        : undefined
    },
    keys: {
      value: summary ? formatCount(summary.keys.generated) : '',
      label: tr('keys', { days: summary?.periodDays ?? 30 }),
      emptyHint: tr('emptyKeysHint'),
      breakdown: summary
        ? [
            { label: tr('keysActive', { count: summary.keys.active }), tone: 'primary' },
            { label: tr('keysInactive', { count: summary.keys.inactive }) },
            { label: tr('keysDeleted', { count: summary.keys.deleted }), tone: 'negative' }
          ]
        : undefined
    }
  }
}

const CARD_ORDER: CardId[] = ['apiCalls', 'latency', 'successRate', 'keys']

/**
 * "Your usage" row: API calls, average latency, success rate and keys
 * generated in the last 30 days. Rendered only when the backend serves the
 * usage summary; while it does not (SAIV-11376 follow-up) the whole section
 * stays hidden so the hero CTAs remain the primary path.
 */
export function UsageSection() {
  const { t } = useTranslation()
  const { status, data, refetch } = useUsageSummary()
  const cards = useCards(data)

  if (status === 'unavailable') return null

  return (
    <section
      className={`${styles.panel} ${styles.usage}`}
      aria-labelledby="dp-usage-title"
      aria-busy={status === 'loading'}
      data-testid="dp-usage"
      data-status={status}
    >
      <div className={styles.usageHeader}>
        <h2 id="dp-usage-title" className={styles.panelTitle}>
          {t('developerPlatform.dashboard.usage.title')}
        </h2>
        <Tooltip title={t('developerPlatform.dashboard.usage.analyticsSoon')}>
          <a
            className={styles.usageLink}
            href="#"
            aria-disabled="true"
            onClick={event => event.preventDefault()}
            data-testid="dp-usage-analytics"
          >
            {t('developerPlatform.dashboard.usage.viewAnalytics')}
            <Icon icon="ArrowSmallRight" size={16} />
          </a>
        </Tooltip>
      </div>

      {status === 'error' ? (
        <Alert
          type="error"
          showIcon
          message={t('developerPlatform.dashboard.usage.errorTitle')}
          description={t('developerPlatform.dashboard.usage.errorText')}
          action={
            <Button size="small" onClick={refetch} data-testid="dp-usage-retry">
              {t('developerPlatform.dashboard.usage.retry')}
            </Button>
          }
          data-testid="dp-usage-error"
        />
      ) : (
        <div className={styles.usageGrid}>
          {CARD_ORDER.map(id => (
            <StatCard
              key={id}
              {...CARD_ICONS[id]}
              {...cards[id]}
              loading={status === 'loading'}
              empty={status === 'empty'}
              data-testid={`dp-usage-${id}`}
            />
          ))}
        </div>
      )}
    </section>
  )
}
